// Thin WebSocket wrapper around the signaling server: auto-reconnect with
// backoff, and a stable per-tab peerId so an admitted client can resume the
// same seat after a network blip. Browser-only — construct it inside an
// effect, never during render.
import type { ClientMessage, Role, ServerMessage, SocketStatus } from "./types"

type Options = {
  url: string
  token: string
  roomId: string
  role: Role
  hostKey?: string | null
  onMessage: (msg: ServerMessage) => void
  onStatus?: (status: SocketStatus) => void
  // The socket came back after a drop and the hello has been re-sent.
  onReconnected?: () => void
}

export function defaultSignalingUrl(): string {
  const proto = window.location.protocol === "https:" ? "wss" : "ws"
  return `${proto}://${window.location.host}/ws`
}

function loadPeerId(roomId: string): string {
  const key = `sanctuary-video-peer:${roomId}`
  try {
    const existing = window.sessionStorage.getItem(key)
    if (existing) return existing
    const fresh = crypto.randomUUID()
    window.sessionStorage.setItem(key, fresh)
    return fresh
  } catch {
    return crypto.randomUUID()
  }
}

export class Signaling {
  readonly peerId: string
  private ws: WebSocket | null = null
  private closed = false
  private attempt = 0
  private timer: ReturnType<typeof setTimeout> | null = null

  constructor(private readonly opts: Options) {
    this.peerId = loadPeerId(opts.roomId)
  }

  connect(): void {
    if (this.closed) return
    this.opts.onStatus?.(this.attempt === 0 ? "connecting" : "reconnecting")
    const ws = new WebSocket(this.opts.url)
    this.ws = ws

    ws.onopen = () => {
      const reconnected = this.attempt > 0
      this.attempt = 0
      this.opts.onStatus?.("connected")
      this.send({
        t: "hello",
        token: this.opts.token,
        role: this.opts.role,
        key: this.opts.hostKey ?? undefined,
        peerId: this.peerId,
      })
      const queued = this.pending
      this.pending = []
      for (const msg of queued) this.send(msg)
      if (reconnected) this.opts.onReconnected?.()
    }
    ws.onmessage = (event) => {
      let msg: ServerMessage
      try {
        msg = JSON.parse(event.data)
      } catch {
        return
      }
      this.opts.onMessage(msg)
    }
    ws.onclose = () => {
      if (this.closed) return
      this.opts.onStatus?.("reconnecting")
      const delay = Math.min(1000 * 2 ** this.attempt++, 15000)
      this.timer = setTimeout(() => this.connect(), delay)
    }
    ws.onerror = () => {
      try {
        ws.close()
      } catch {
        /* already closed */
      }
    }
  }

  // Sent while the socket was down. An SDP offer or ICE candidate dropped on
  // the floor during a reconnect leaves both peers waiting on each other for
  // ever (seen: a phone back from the background, stuck in have-local-offer),
  // so anything sent while not OPEN waits here and goes out after the next
  // hello. Bounded: a long outage should not replay a minute of candidates.
  private pending: ClientMessage[] = []
  private static readonly PENDING_MAX = 64

  send(msg: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg))
    } else if (!this.closed && msg.t === "signal") {
      if (this.pending.length >= Signaling.PENDING_MAX) this.pending.shift()
      this.pending.push(msg)
    }
  }

  close(): void {
    this.closed = true
    if (this.timer) clearTimeout(this.timer)
    this.opts.onStatus?.("closed")
    try {
      this.ws?.close()
    } catch {
      /* already closed */
    }
  }
}
